import React, { useState, useEffect } from 'react';
import axiosInstance from '../../utils/axios';
import { toast } from 'react-hot-toast';
import {
  DocumentTextIcon,
  ArrowUpTrayIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  PencilIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

/**
 * CompanyDocuments Component
 * Displays and manages documents for a company
 * 
 * Props:
 * - companyId: string (required) - Company ID
 * - canUpload: boolean - Whether user can upload documents
 * - canDelete: boolean - Whether user can delete documents (admin only)
 */
const CompanyDocuments = ({ companyId, canUpload = false, canDelete = false }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('other');
  const [uploadDescription, setUploadDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);

  const categories = [
    { value: 'contract', label: 'חוזה' },
    { value: 'invoice', label: 'חשבונית' },
    { value: 'license', label: 'רישיון' },
    { value: 'tax_document', label: 'מסמך מס' },
    { value: 'other', label: 'אחר' }
  ];

  useEffect(() => {
    if (companyId) {
      fetchDocuments();
    }
  }, [companyId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/companies/${companyId}/documents`);
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('שגיאה בטעינת מסמכים');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        toast.error('גודל הקובץ חורג מ-50MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('אנא בחר קובץ להעלאה');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('category', uploadCategory);
    formData.append('description', uploadDescription);
    formData.append('is_private', isPrivate);

    try {
      setUploading(true);
      await axiosInstance.post(
        `/api/companies/${companyId}/documents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('המסמך הועלה בהצלחה');
      setShowUploadModal(false);
      setSelectedFile(null);
      setUploadDescription('');
      setUploadCategory('other');
      setIsPrivate(false);
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error.response?.data?.message || 'שגיאה בהעלאת המסמך');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק מסמך זה?')) {
      return;
    }

    try {
      await axiosInstance.delete(`/api/companies/documents/${documentId}`);

      toast.success('המסמך נמחק בהצלחה');
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('שגיאה במחיקת המסמך');
    }
  };

  const handleDownload = async (documentId, fileName) => {
    try {
      const response = await axiosInstance.get(`/api/companies/documents/${documentId}/download`);
      const downloadUrl = response.data.download_url;
      const originalName = response.data.file_name || fileName;

      // iOS Safari compatible download
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                    (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
      
      if (isIOS) {
        // On iOS, directly navigate to the URL (triggers download or opens in browser)
        window.location.href = downloadUrl;
      } else {
        // On desktop/Android, use anchor click with original filename
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = originalName; // Preserve original Hebrew filename
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('שגיאה בהורדת המסמך');
    }
  };

  const handleUpdate = async (documentId) => {
    try {
      await axiosInstance.put(
        `/api/companies/documents/${documentId}`,
        {
          category: editingDoc.category,
          description: editingDoc.description,
          is_private: editingDoc.is_private
        }
      );

      toast.success('המסמך עודכן בהצלחה');
      setEditingDoc(null);
      fetchDocuments();
    } catch (error) {
      console.error('Error updating document:', error);
      toast.error('שגיאה בעדכון המסמך');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getCategoryLabel = (value) => {
    return categories.find(c => c.value === value)?.label || 'אחר';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" dir="rtl">
      {/* Header with Upload Button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">מסמכי חברה</h3>
        {canUpload && (
          <button
            onClick={() => setShowUploadModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <ArrowUpTrayIcon className="ml-2 h-5 w-5" />
            העלה מסמך
          </button>
        )}
      </div>

      {/* Documents List */}
      {documents.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">אין מסמכים</h3>
          <p className="mt-1 text-sm text-gray-500">
            {canUpload ? 'העלה מסמך ראשון' : 'טרם הועלו מסמכים לחברה זו'}
          </p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {documents.map((doc) => (
              <li key={doc.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                {editingDoc?.id === doc.id ? (
                  /* Edit Mode */
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        קטגוריה
                      </label>
                      <select
                        value={editingDoc.category}
                        onChange={(e) => setEditingDoc({ ...editingDoc, category: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        {categories.map(cat => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        תיאור
                      </label>
                      <textarea
                        value={editingDoc.description || ''}
                        onChange={(e) => setEditingDoc({ ...editingDoc, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        rows="2"
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`edit-private-${doc.id}`}
                        checked={editingDoc.is_private}
                        onChange={(e) => setEditingDoc({ ...editingDoc, is_private: e.target.checked })}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`edit-private-${doc.id}`} className="mr-2 block text-sm text-gray-900">
                        מסמך פרטי (רק אדמין)
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleUpdate(doc.id)}
                        className="px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                      >
                        שמור
                      </button>
                      <button
                        onClick={() => setEditingDoc(null)}
                        className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                      >
                        ביטול
                      </button>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <DocumentTextIcon className="h-8 w-8 text-gray-400 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {doc.original_name}
                          </p>
                          <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                            <span className="px-2 py-1 bg-gray-100 rounded">
                              {getCategoryLabel(doc.category)}
                            </span>
                            <span>{formatFileSize(doc.file_size)}</span>
                            <span>
                              {new Date(doc.created_at).toLocaleDateString('he-IL')}
                            </span>
                            {doc.is_private && (
                              <span className="px-2 py-1 bg-red-100 text-red-800 rounded">
                                פרטי
                              </span>
                            )}
                          </div>
                          {doc.description && (
                            <p className="mt-1 text-sm text-gray-500 truncate">
                              {doc.description}
                            </p>
                          )}
                          {doc.uploader && (
                            <p className="mt-1 text-xs text-gray-400">
                              הועלה ע"י: {doc.uploader.first_name} {doc.uploader.last_name}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mr-4">
                      <button
                        onClick={() => handleDownload(doc.id, doc.original_name)}
                        className="p-2 text-primary-600 hover:text-primary-900 hover:bg-primary-50 rounded-full"
                        title="הורד"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5" />
                      </button>
                      {canDelete && (
                        <>
                          <button
                            onClick={() => setEditingDoc(doc)}
                            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full"
                            title="ערוך"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(doc.id)}
                            className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-full"
                            title="מחק"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" dir="rtl">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowUploadModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-right overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">העלאת מסמך</h3>
                  <button
                    onClick={() => setShowUploadModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      בחר קובץ
                    </label>
                    <input
                      type="file"
                      onChange={handleFileSelect}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                      className="w-full"
                    />
                    {selectedFile && (
                      <p className="mt-2 text-sm text-gray-500">
                        {selectedFile.name} ({formatFileSize(selectedFile.size)})
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      קטגוריה
                    </label>
                    <select
                      value={uploadCategory}
                      onChange={(e) => setUploadCategory(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    >
                      {categories.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      תיאור (אופציונלי)
                    </label>
                    <textarea
                      value={uploadDescription}
                      onChange={(e) => setUploadDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      rows="3"
                      placeholder="הוסף תיאור למסמך..."
                    />
                  </div>

                  {canDelete && (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is-private"
                        checked={isPrivate}
                        onChange={(e) => setIsPrivate(e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is-private" className="mr-2 block text-sm text-gray-900">
                        מסמך פרטי (רק אדמין יוכל לצפות)
                      </label>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                <button
                  onClick={handleUpload}
                  disabled={!selectedFile || uploading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? 'מעלה...' : 'העלה'}
                </button>
                <button
                  onClick={() => setShowUploadModal(false)}
                  disabled={uploading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  ביטול
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyDocuments;

